import Checkbox from '@/components/base/form/input/checkbox';
import { ConsentCheckbox } from '@/features/login/components/consents-section/consent-checkbox';
import { ExpandedText } from '@/features/login/components/consents-section/expanded-text';
import {
  type LoginConsentStore,
  type LoginConsents,
  useLoginConsentStore,
} from '@/features/login/store/login-consents-store';
import { Link } from '@/navigation';
import { Redirect, getRedirectPath } from '@/utils/redirects';
import { useLocale, useTranslations } from 'next-intl';
import type { ChangeEvent } from 'react';

const selector = ({
  consents,
  acceptAllConsents,
  setConsent,
}: LoginConsentStore) => ({
  consents,
  acceptAllConsents,
  setConsent,
});

export const ConsentsSection = () => {
  const t = useTranslations('login.consents');
  const locale = useLocale();

  const { consents, acceptAllConsents, setConsent } =
    useLoginConsentStore(selector);

  const onAcceptAll = () => {
    acceptAllConsents();
  };

  const isAllAccepted = Object.values(consents).every((value) => value);
  const isAtLeastOneAccepted = Object.values(consents).some((value) => value);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as keyof LoginConsents;
    setConsent(name, e.target.checked);
  };

  const privacyPolicy = getRedirectPath(Redirect.PRIVACY, locale);
  const regulations = getRedirectPath(Redirect.REGULATIONS, locale);

  return (
    <>
      <div className="pb-xs">
        <Checkbox
          label={t('select_all')}
          id="select_all"
          checked={isAllAccepted}
          indeterminate={isAtLeastOneAccepted}
          onClick={onAcceptAll}
          testId="select_all"
          size="small"
          className="py-xxs"
          readOnly
          radioCheckboxClassname="text-small-bold"
        />
        <div className="mt-xxs flex flex-col pl-m text-small">
          <ConsentCheckbox
            checked={consents.marketing}
            label={t('marketing_label')}
            expandedText={t.rich('marketing_label_expanded', {
              link: (chunks) => (
                <Link href={privacyPolicy} target="_blank">
                  <span className="underline">{chunks}</span>
                </Link>
              ),
              br: () => <br />,
              email: (chunks) => (
                <a className="underline" href={`mailto:${chunks}`}>
                  {chunks}
                </a>
              ),
            })}
            onChange={onChange}
            className="pt-xxs"
            name="marketing"
          />
          <ConsentCheckbox
            checked={consents.terms}
            name="terms"
            testId="t&c"
            label={t.rich('terms_and_conditions_label', {
              link: (chunks) => (
                <Link href={regulations} target="_blank">
                  <span className="underline">{chunks}</span>
                </Link>
              ),
              link2: (chunks) => (
                <Link href={privacyPolicy} target="_blank">
                  <span className="underline">{chunks}</span>
                </Link>
              ),
            })}
            onChange={onChange}
            className="pt-xxs"
            required
          />
        </div>
      </div>

      <ExpandedText
        className="text-default text-small"
        text={t('data_processing_info')}
        isLink
        href={privacyPolicy}
      />
    </>
  );
};
